<script setup lang="ts">

import {inject, ref, computed} from 'vue';
import {SvgIcon} from '../../../svg.ts';

import FileTreeItem from '../FileTreeItem.vue';
import FileTreeHierarchyGroup from './FileTreeHierarchyGroup.vue';
import type {MultiPageDiff, ProjectDiff, ProjectPageDiff} from '../../types/file_tree.ts';
import {statusIcon, statusClasses} from './utils.ts';
import {LOADING_STATUS} from '../../types/file_tree.ts';
import type {Store} from '../../store.ts';

const store = inject<Store>('store');

const {icon, item, forceExpand = false} = defineProps<{
  icon: string,
  item: ProjectDiff | MultiPageDiff,
  forceExpand?: boolean,
}>();

const collapsed = ref(false);
const isExpanded = computed(() => forceExpand || !collapsed.value);

const childrenByPageId = computed(() => {
  const map = new Map<string, ProjectPageDiff>();
  for (const child of item.children ?? []) {
    map.set(child.pageId, child);
  }
  return map;
});

</script>

<template>
  <div>
    <div
      class="tw:flex tw:gap-1 tw:p-1 tw:items-center tw:hover:cursor-pointer tw:hover:bg-hover"
      :title="item.name"
      :class="{'tw:text-text tw:bg-active tw:rounded-[4px]': item.nameHash === store.activeHash.value}"
    >
      <div
        class="tw:cursor-pointer"
        @click.stop="collapsed = !collapsed"
      >
        <SvgIcon :name="isExpanded ? 'octicon-chevron-down' : 'octicon-chevron-right'" />
      </div>
      <a
        :title="item.headPath"
        :href="`#diff-${item.nameHash}`"
        class="tw:flex tw:items-center tw:gap-1 tw:text-text tw:no-underline tw:grow"
        :class="{'tw:text-text-light-3': item.isViewed}"
      >
        <SvgIcon
          class="text primary"
          :name="icon"
        />
        <span
          class="tw:text-ellipsis tw:overflow-hidden tw:whitespace-nowrap"
          :class="{'tw:text-text-light-3': item.isViewed}"
        >{{ item.name }}</span>
        <div
          v-if="item.loadingStatus === LOADING_STATUS.IN_PROGRESS"
          class="is-loading tw:h-8 tw:ml-auto tw:mr-4"
        />
        <SvgIcon
          v-else
          :name="statusIcon(item.status)"
          class="tw:ml-auto"
          :class="statusClasses(item.status)"
        />
      </a>
    </div>
    <!-- Children -->
    <div
      v-show="isExpanded"
      class="tw:ml-3 tw:border-l tw:pl-3 tw:border-secondary"
    >
      <template v-if="item.hierarchy?.length > 0">
        <FileTreeHierarchyGroup
          v-for="group in item.hierarchy"
          :key="group.groupId"
          :group="group"
          :children-by-page-id="childrenByPageId"
          :force-expand="forceExpand"
        />
      </template>
      <template v-else>
        <FileTreeItem
          v-for="child in item.children"
          :key="child.name"
          :item="child"
          :force-expand="forceExpand"
        />
      </template>
    </div>
  </div>
</template>
